import { Link } from "react-router-dom";

import EntityCampaignsPanel from "components/sidebar/sections/EntityCampaignsPanel";
import {
  CustomFieldsPanelSection,
  PanelField,
  PanelSection,
} from "components/sidebar/sections/InfoPanel";
import TagsPanelSection from "components/sidebar/sections/TagsPanelSection";
import { NO_COUNTRY, NO_COUNTRY_TEXT } from "helpers/countries";

const pointOfContactLabel = poc => {
  const roles = [];
  if (poc.is_launch_contact) roles.push("Launch");
  if (poc.is_reporting_contact) roles.push("Reporting");
  return roles.length
    ? `${poc.point_of_contact_name} (${roles.join(", ")})`
    : poc.point_of_contact_name;
};

// Read-only information for the revenue opportunity detail rail. The tag
// dropdown in the tags section is the only editable affordance; a locked
// opportunity disables it. The record fetch does not accept a
// show_disabled_tags parameter, so disabled tags are filtered client-side from
// the applied-tag list and the dropdown reveals them via its own prop.
const RevenueOpportunityInfoPanel = ({ model, showDisabledTags, onUpdate }) => {
  const modelTags = showDisabledTags
    ? (model?.tags ?? [])
    : (model?.tags ?? []).filter(({ enabled }) => enabled);

  const pointsOfContact = (model?.points_of_contact ?? [])
    .map(pointOfContactLabel)
    .join(", ");

  return (
    <>
      <PanelSection>
        <PanelField label="Revenue Opportunity ID">{model?.id}</PanelField>
        <PanelField label="Lock State">
          {model?.is_locked ? "Locked" : "Unlocked"}
        </PanelField>
        <PanelField label="Entity Name">
          {model?.entity_id && (
            <Link to={`/entities/${model.entity_id}`}>{model.entity_name}</Link>
          )}
        </PanelField>
        <PanelField label="Cases">
          {(model?.cases ?? []).map(c => (
            <Link key={c.id} to={`/cases/${c.id}`}>
              {c.name}
            </Link>
          ))}
        </PanelField>
        <PanelField label="Contact">
          {model?.contact_full_name && (
            <Link to={`/contacts/${model.contact_id}`}>
              {model.contact_full_name}
            </Link>
          )}
        </PanelField>
        <PanelField label="Assignee">{model?.assignee_name}</PanelField>
        <PanelField label="Recipient Type">{model?.recipient_type}</PanelField>
        <PanelField label="Recipient">{model?.recipient_name}</PanelField>
        <PanelField label="Region">{model?.region_name}</PanelField>
        <PanelField label="Country">
          {model?.country === NO_COUNTRY ? NO_COUNTRY_TEXT : model?.country}
        </PanelField>
        <PanelField label="Billing To">{model?.billing_to}</PanelField>
        <PanelField label="Invoice #">{model?.invoice?.join(", ")}</PanelField>
        <PanelField label="Invoice Date">{model?.invoice_date}</PanelField>
        <PanelField label="P. O. #">
          {model?.purchase_order?.join(", ")}
        </PanelField>
        <PanelField label="S. O. #">
          {model?.sales_order?.join(", ")}
        </PanelField>
        <PanelField label="S. O. Date">{model?.sales_order_date}</PanelField>
        <PanelField label="Status">{model?.status_full_name}</PanelField>
        <PanelField label="Effective Date">{model?.effective_date}</PanelField>
        <PanelField label="Launch Date">{model?.launch_date}</PanelField>
        <PanelField label="Points of Contact">{pointsOfContact}</PanelField>
        <PanelField label="Actionable Machines">
          {model?.actionable_machines}
        </PanelField>
        <PanelField label="Invoice Total Value">
          {model?.invoice_total_value}
        </PanelField>
      </PanelSection>

      <TagsPanelSection
        title="Tags"
        modelType="RevenueOpportunity"
        record={model}
        appliedTags={modelTags}
        showDisabledTags={showDisabledTags}
        onUpdate={onUpdate}
        disabled={model?.is_locked}
        disabledTooltip="This revenue opportunity is locked."
      />

      <CustomFieldsPanelSection
        title="Custom Fields"
        items={model?.custom_fields}
        modelType="RevenueOpportunity"
      />

      {model?.entity_id && <EntityCampaignsPanel entityId={model.entity_id} />}
    </>
  );
};

export default RevenueOpportunityInfoPanel;
